import useNoindex from "../lib/useNoindex";
import React, { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { Gavel, ArrowRight, CheckCircle2, Clock, Lock } from "lucide-react";

export default function JudgeDashboard() {
  useNoindex();
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [round, setRound] = useState(null);

  const load = useCallback(async (r) => {
    try {
      const q = r ? `?round=${r}` : "";
      const { data } = await api.get(`/judge/dashboard${q}`);
      setData(data);
      setRound(data.assigned_round);
    } catch (err) { toast.error(errorMessage(err)); }
  }, []);

  useEffect(() => { if (user?.role === "judge") load(round); }, [user, round, load]);

  if (loading) return <div className="App"><Navbar /><div className="min-h-[60vh] flex items-center justify-center"><span className="spinner" /></div></div>;
  if (!user || user.role !== "judge") return <Navigate to="/login" replace />;
  if (!data) return <div className="App"><Navbar /><div className="min-h-[60vh] flex items-center justify-center"><span className="spinner" /></div></div>;

  const pct = data.total > 0 ? Math.round((data.scored / data.total) * 100) : 0;

  return (
    <div className="App" data-testid="judge-dashboard-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center gap-3 mb-2"><Gavel className="w-5 h-5 text-brand" /><span className="label-mono">Judge Console</span></div>
        <h1 className="font-display text-4xl sm:text-5xl text-white">Welcome, <span className="text-brand">{user.name}</span></h1>

        {!data.assigned_round ? (
          <div className="mt-8 card-surface">
            <p className="text-gray-400">You haven't been assigned to a judging round yet. Please contact the admin.</p>
          </div>
        ) : (
          <>
            {data.scoring_locked && (
              <div className="mt-6 card-surface border-l-2 border-amber-500 bg-amber-500/5" data-testid="judging-closed-banner">
                <div className="flex items-center gap-3">
                  <Lock className="w-5 h-5 text-amber-400" />
                  <div>
                    <div className="text-white font-medium">Judging closed{data.judging_deadline ? ` on ${new Date(data.judging_deadline).toLocaleString()}` : ""}.</div>
                    <div className="text-sm text-gray-400">Scores are now locked. You can view submissions but can no longer add or edit scores.</div>
                  </div>
                </div>
              </div>
            )}
            {data.assigned_rounds && data.assigned_rounds.length > 1 && (
              <div className="mt-6 flex border border-white/10 w-fit" data-testid="judge-round-tabs">
                {data.assigned_rounds.map(r => (
                  <button key={r} onClick={() => setRound(r)}
                    className={`px-5 py-2 font-mono text-xs uppercase tracking-widest transition-colors ${round === r ? "bg-brand text-black" : "text-gray-400 hover:text-white"}`}
                    data-testid={`judge-round-tab-${r}`}>Round {r}</button>
                ))}
              </div>
            )}
            <div className="mt-6 grid sm:grid-cols-3 gap-4">
              <Stat label="Assigned Round" value={`Round ${data.assigned_round}`} testid="stat-round" />
              <Stat label="Total to Score" value={data.total} testid="stat-total" />
              <Stat label="Remaining" value={data.remaining} accent testid="stat-remaining" />
            </div>

            <div className="mt-6 card-surface">
              <div className="flex items-center justify-between mb-3">
                <div className="label-mono">Progress</div>
                <div className="font-mono text-sm text-gray-400">{data.scored} / {data.total} scored ({pct}%)</div>
              </div>
              <div className="w-full h-2 bg-white/5 overflow-hidden">
                <div className="h-full bg-brand transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>

            <div className="mt-10">
              <div className="label-mono mb-3">Teams</div>
              {data.teams.length === 0 ? (
                <div className="card-surface text-gray-500">No submitted teams to score yet for Round {data.assigned_round}.</div>
              ) : (
                <div className="space-y-2" data-testid="teams-to-score">
                  {data.teams.map(t => (
                    <div key={t.team_id} className="border border-white/10 p-4 flex items-center justify-between flex-wrap gap-3 card-hover" data-testid={`judge-team-${t.team_id}`}>
                      <div>
                        <div className="font-display text-white">{t.team_name}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{t.project_title}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        {t.scored ? (
                          <span className="badge badge-accepted flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> scored</span>
                        ) : (
                          <span className="badge badge-invited flex items-center gap-1"><Clock className="w-3 h-3" /> pending</span>
                        )}
                        {!t.scored && (
                          <Link to={`/judge/score/${t.team_id}?round=${data.assigned_round}`} className="btn-primary !py-2 !px-3 text-xs flex items-center gap-1" data-testid={`judge-score-${t.team_id}`}>
                            Score <ArrowRight className="w-3 h-3" />
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

const Stat = ({ label, value, accent, testid }) => (
  <div className={`card-surface ${accent ? "border-l-2 border-brand" : ""}`} data-testid={testid}>
    <div className="label-mono">{label}</div>
    <div className="font-display text-3xl text-white mt-1">{value}</div>
  </div>
);
